
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const AutomationPage = () => {
  const [activeWorkflow, setActiveWorkflow] = useState('lead-nurturing');

  const workflows = [
    {
      id: 'lead-nurturing',
      title: 'Lead Nurturing Automation',
      description: 'Automatically follow up with leads based on their behavior and engagement level.',
      steps: [
        'Lead enters system',
        'Send welcome email',
        'Track engagement',
        'Send targeted content',
        'Schedule follow-up calls',
        'Convert to client'
      ],
      benefits: ['80% increase in lead conversion', '50% reduction in manual work', '24/7 lead engagement']
    },
    {
      id: 'document-management',
      title: 'Document Management',
      description: 'Streamline contract creation, signing, and storage with intelligent automation.',
      steps: [
        'Generate contracts',
        'Send for e-signature',
        'Track signing status',
        'Store in cloud',
        'Send notifications',
        'Archive completed docs'
      ],
      benefits: ['90% faster document processing', 'Zero paperwork errors', 'Automatic compliance']
    },
    {
      id: 'client-onboarding',
      title: 'Client Onboarding',
      description: 'Create seamless onboarding experiences for new clients with automated workflows.',
      steps: [
        'Welcome new client',
        'Collect preferences',
        'Set up property alerts',
        'Schedule consultation',
        'Assign agent',
        'Begin property search'
      ],
      benefits: ['95% client satisfaction', '60% faster onboarding', 'Consistent experience']
    },
    {
      id: 'marketing-campaigns',
      title: 'Marketing Campaigns',
      description: 'Automate your marketing efforts with targeted campaigns and personalized content.',
      steps: [
        'Segment audience',
        'Create content',
        'Schedule campaigns',
        'Track engagement',
        'Optimize performance',
        'Generate reports'
      ],
      benefits: ['3x higher engagement', '40% more qualified leads', 'Automated ROI tracking']
    }
  ];

  const automationTools = [
    {
      icon: 'ri-mail-send-line',
      title: 'Email Automation',
      description: 'Personalized email sequences that nurture leads and keep clients engaged.',
      features: ['Drip campaigns', 'Behavioral triggers', 'A/B testing', 'Analytics']
    },
    {
      icon: 'ri-calendar-event-line',
      title: 'Appointment Scheduling',
      description: 'Automated booking system that syncs with your calendar and sends reminders.',
      features: ['Calendar sync', 'Automatic reminders', 'Rescheduling', 'Time zone handling']
    },
    {
      icon: 'ri-file-text-line',
      title: 'Document Generation',
      description: 'Create contracts, agreements, and forms automatically with client data.',
      features: ['Template library', 'Data auto-fill', 'E-signature integration', 'Version control']
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Management',
      description: 'Automated call routing, voicemail transcription, and follow-up scheduling.',
      features: ['Smart routing', 'Voicemail to text', 'Call recording', 'Follow-up automation']
    },
    {
      icon: 'ri-notification-3-line',
      title: 'Smart Notifications',
      description: 'Intelligent alerts for important events, deadlines, and opportunities.',
      features: ['Priority alerts', 'Multi-channel delivery', 'Custom rules', 'Escalation paths']
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Performance Analytics',
      description: 'Automated reporting and insights to optimize your business processes.',
      features: ['Real-time dashboards', 'Custom reports', 'Trend analysis', 'ROI tracking']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">
                Workflow Automation Suite
              </h1>
              <p className="text-xl mb-8">
                Streamline your real estate operations with intelligent automation that handles repetitive tasks, 
                nurtures leads, and manages client relationships while you focus on closing deals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/demo" 
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  See Automation Demo
                </Link>
                <Link 
                  to="/contact" 
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/ba63c17a738ba8f2ba54304100066469.jpg"
                alt="Workflow Automation Dashboard"
                className="w-full rounded-lg shadow-2xl object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Automation Tools Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Automation Tools</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to automate your real estate business and increase efficiency.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {automationTools.map((tool, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${tool.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{tool.title}</h3>
                <p className="text-gray-600 mb-4">{tool.description}</p>
                <ul className="space-y-2">
                  {tool.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Showcase */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pre-Built Workflow Templates</h2>
            <p className="text-xl text-gray-600">
              Choose from proven automation workflows designed specifically for real estate professionals.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            {workflows.map((workflow) => (
              <button
                key={workflow.id}
                onClick={() => setActiveWorkflow(workflow.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeWorkflow === workflow.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-100 hover:text-blue-600 shadow-md'
                }`}
              >
                {workflow.title}
              </button>
            ))}
          </div>
          
          {workflows.map((workflow) => (
            activeWorkflow === workflow.id && (
              <div key={workflow.id} className="bg-white rounded-xl shadow-lg p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">{workflow.title}</h3>
                    <p className="text-gray-600 mb-6">{workflow.description}</p>
                    
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Benefits:</h4>
                    <ul className="space-y-2">
                      {workflow.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-center text-gray-700">
                          <i className="ri-check-line text-green-500 mr-3"></i>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Workflow Steps:</h4>
                    <div className="space-y-4">
                      {workflow.steps.map((step, index) => (
                        <div key={index} className="flex items-center">
                          <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-4">
                            {index + 1}
                          </div>
                          <span className="text-gray-700">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Calculate Your ROI</h2>
              <p className="text-xl">
                See how much time and money you can save with automation.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">15+</div>
                <div className="text-blue-100">Hours Saved Per Week</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">300%</div>
                <div className="text-blue-100">Increase in Lead Response</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$50K+</div>
                <div className="text-blue-100">Additional Annual Revenue</div>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Link 
                to="/contact" 
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Custom ROI Analysis
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Seamless Integrations</h2>
            <p className="text-xl text-gray-600">
              Connect with your existing tools and platforms for a unified workflow.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {[
              'Salesforce', 'HubSpot', 'Zillow', 'MLS', 'Gmail', 'Outlook',
              'Slack', 'Zapier', 'DocuSign', 'Dropbox', 'Google Drive', 'Calendly'
            ].map((integration, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-2 flex items-center justify-center">
                  <i className="ri-apps-line text-xl text-gray-600"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{integration}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AutomationPage;
